import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Stack,
  Paper,
  Avatar,
  Tooltip,
  useTheme,
  Accordion,
  IconButton,
  Typography,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';

import { useToast } from 'src/hooks/useToast';

import convertArrayReturnToHTML from 'src/utils/convertArrayReturn';

import Iconify from 'src/components/iconify/Iconify';

import { scrollbarStyle2 } from 'src/sections/style';

import fileDownload from '../../../utils/fileDownload';
import { genericApiToaster } from '../../../services/utils';
import { getFileByName } from '../../../services/file.service';
import convertLineReturnToHTML from '../../../utils/convertLineReturn';

FormAnswersModal.propTypes = {
  row: PropTypes.object,
  hideModal: PropTypes.func,
};

export default function FormAnswersModal({ row, hideModal }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState([]);
  const { showToast, hideToast } = useToast();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const generateCSV = () => {
    let csvContent = `${t('pages.ticketDetails.question')},${t(
      'pages.ticketDetails.answer'
    )}\n`;
    row?.data?.forEach((section) => {
      section.questions?.forEach((q) => {
        const questionText = `"${q.text.replace(/"/g, '""')}"`;
        const answerText = Array.isArray(q.inputValue)
          ? `"${q.inputValue.join(', ').replace(/"/g, '""')}"`
          : `"${String(q.inputValue || '').replace(/"/g, '""')}"`;
        csvContent += `${questionText},${answerText}\n`;
      });
    });
    return csvContent;
  };

  const dataInCSV = generateCSV();

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded((prev) =>
      isExpanded ? [...prev, panel] : prev.filter((p) => p !== panel)
    );
  };

  const handleDownloadClick = async (e) => {
    const name = e.currentTarget.value;
    genericApiToaster(
      getFileByName,
      name,
      '',
      t('errorMessages.couldntDownloadFile'),
      showToast,
      hideToast,
      t,
      (file) => fileDownload(file, name)
    );
  };

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '80vh' }}
    >
      {/* 1. TOP ACTION HEADER */}
      <Box
        sx={{
          p: 2,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#F8F9FA',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary' }}>
          {t('pages.ticketDetails.formResponses')}
        </Typography>

        <Tooltip title={t('ticket.downloadAnswers')} placement="top">
          <IconButton
            href={`data:text/csv;charset=utf-8,${encodeURIComponent(dataInCSV)}`}
            size="small"
            sx={{
              color: theme.palette.success.main,
              bgcolor: `${theme.palette.success.main}20`,
              borderRadius: '8px',
              '&:hover': { bgcolor: `${theme.palette.success.main}30` },
            }}
          >
            <Iconify icon="solar:download-square-bold-duotone" width={30} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* 2. SCROLLABLE CONTENT AREA */}
      <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto', ...scrollbarStyle2 }}>
        {row?.data?.map((section) => (
          <Box
            key={section.sectionId}
            sx={{
              mb: 2,
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Accordion
              expanded={expanded.includes(section.sectionId)}
              onChange={handleChange(section.sectionId)}
            >
              <AccordionSummary
                expandIcon={<Iconify icon="ooui:expand" width={15} />}
                sx={{
                  bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#F6F6F6',
                  '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F0F0F0' },
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {section.sectionName}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: isDark ? 'transparent' : '#F6F6F6' }}>
                <Box component="ul" sx={{ pl: 2, listStyle: 'none' }}>
                  {section.questions?.map((question, index) => {
                    let icon = 'bx:file';
                    if (question.type === 'FileInput') {
                      const extensions = ['.jpeg', '.jpg', '.png', '.gif'];
                      extensions.forEach((ext) => {
                        if (
                          question?.inputValue &&
                          question?.inputValue?.length > 0 &&
                          question?.inputValue[0]?.endsWith(ext)
                        ) {
                          icon = 'ph:image';
                        }
                      });
                    }

                    return (
                      <Box
                        component="li"
                        key={index}
                        sx={{ borderRadius: 2, mb: 2, overflow: 'hidden' }}
                      >
                        <Paper
                          sx={{
                            p: 2,
                            borderRadius: 0,
                            bgcolor: isDark ? theme.palette.background.paper : '#DEE6ED',
                            boxShadow: 'none',
                          }}
                        >
                          <Typography variant="body2">
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              sx={{ mr: 0.5 }}
                              gutterBottom
                            >
                              {t('pages.ticketDetails.question')} :
                            </Typography>
                            {question.text}
                          </Typography>
                        </Paper>
                        <Paper
                          sx={{
                            p: 2,
                            borderRadius: 0,
                            boxShadow: 'none',
                            bgcolor: isDark ? theme.palette.background.paper : undefined,
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            gutterBottom
                          >
                            {t('pages.ticketDetails.answer')} :
                          </Typography>
                          <Typography variant="body2">
                            {Array.isArray(question.inputValue) ? (
                              question.type === 'FileInput' ? (
                                <Stack direction="row" alignItems="center" spacing={2}>
                                  <Avatar>
                                    <Iconify icon={icon} />
                                  </Avatar>
                                  <Typography
                                    variant="caption"
                                    sx={{ wordBreak: 'break-all' }}
                                  >
                                    {question.inputValue}
                                  </Typography>
                                  <IconButton
                                    value={question.inputValue}
                                    onClick={handleDownloadClick}
                                    edge="end"
                                  >
                                    <Iconify icon="material-symbols:download" />
                                  </IconButton>
                                </Stack>
                              ) : (
                                convertArrayReturnToHTML(question.inputValue)
                              )
                            ) : typeof question.inputValue === 'string' ? (
                              convertLineReturnToHTML(question.inputValue)
                            ) : (
                              question.inputValue
                            )}
                          </Typography>
                        </Paper>
                      </Box>
                    );
                  })}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
